import { useSyncExternalStore } from "react";
import type { ApolloClient } from "@apollo/client";
import { toast } from "sonner";
import { BulkAutoEnrollDocument } from "@/graphql/organizations.generated";
import { toEnrollRow, type AutoEnrollCsvRow } from "@/lib/autoEnrollCsv";
import { isTeardownError } from "@/lib/queryErrors";

/**
 * An auto-enrollment upload, held outside the component that started it.
 *
 * The run used to be local state inside `OrganizationAutoEnroll`, which made leaving the page
 * destroy it twice over. The results were gone — they were only written once, after the last of
 * 85 batches — and the in-flight request was gone with them, because a `useMutation` issued by a
 * component is aborted when that component unmounts (see `isTeardownError`). An 8,500 row upload
 * is about ten minutes of sending, so "switch to the Users tab to see if it is working" cancelled
 * the thing it was checking on.
 *
 * So the run lives here: a module-level store, the same shape `useListView` uses for the list
 * mode, and the batch loop issues its mutations through the client directly rather than through a
 * hook bound to a mounted component. Navigating away now leaves it running, and coming back
 * re-renders whatever it has reached. It is deliberately not persisted to storage — the loop is
 * this page's JavaScript, so a reload ends the run, and a record of a run nobody is driving any
 * more would claim progress that has stopped.
 */

/**
 * What one row is doing, from parse to outcome.
 *
 * Two of these are the client's own and never come back from the server. `QUEUED` is a row whose
 * batch has not been sent yet, which on 85 batches is most of them for most of the run, and
 * `SENDING` is the batch in flight. Without them a row in progress is indistinguishable from one
 * the server has answered for.
 *
 * The rest are the server's `AutoEnrollStatus` enum, and they say what this upload *did* to a row
 * rather than what the person now *is*. That is why none of them is called `INVITED`: "invited" is
 * a roster state, the one an `ENROLLED` row leaves the person in, and `ACTIVATED` leaves them
 * Active instead. The display word is unified at the other end — `STATUS_LABEL` in
 * `OrganizationAutoEnroll` is where a status becomes the word somebody reads.
 *
 * `INVALID` against `NOT_SENT` is the other distinction worth keeping: an invalid row was read and
 * refused, a not-sent row was never seen. One is fixed in the spreadsheet, the other by uploading
 * the same file again.
 */
export type AutoEnrollRowStatus =
    /** Valid, waiting for its batch. */
    | "QUEUED"
    /** Its batch is in flight. */
    | "SENDING"
    /** The upload created an invitation — the roster shows them Invited until they first sign in. */
    | "ENROLLED"
    /** Already had an account, so the upload joined them outright — Active, not Invited. */
    | "ACTIVATED"
    /** Already on this roster; nothing changed. */
    | "DUPLICATE"
    /** The row could not be used. */
    | "INVALID"
    /** Its batch never reached the server. */
    | "NOT_SENT";

export interface AutoEnrollRowResult {
    status: AutoEnrollRowStatus;
    message?: string | null;
}

export interface AutoEnrollRun {
    organizationId: string;
    fileName: string;
    /** Every parsed row, so the preview table renders unchanged after a remount. */
    rows: AutoEnrollCsvRow[];
    /**
     * Keyed by the row's line in the file, not its email.
     *
     * A file with a repeat holds the same address twice, so an email keys two rows: the original,
     * which is valid and gets sent, and the repeat, which does not. Keyed by email the repeat's
     * verdict landed on the original as well, and a perfectly good row read "Duplicate of line 2".
     */
    statuses: Map<number, AutoEnrollRowResult>;
    /** Valid rows this run is sending — the denominator for progress. */
    total: number;
    /** Rows that have reached an outcome, so a caller can show "n of m" without counting. */
    settled: number;
    running: boolean;
}

/*
 * Rows per request.
 *
 * Sized against the GraphQL Lambda's 30 second timeout, not against anything the API enforces —
 * it validates no row count of its own. Each row writes an enrollment, its sub-org targets and
 * possibly a membership, then sends a welcome email, so the batch that fits is an empirical
 * number. This was 200 until a 992-row roster went four batches and died in the fifth on exactly
 * that ceiling (`Duration: 30000.00 ms`), rolling its transaction back and leaving 800 enrolled.
 *
 * 100 halves the work per invocation at the cost of twice as many round trips, which are cheap.
 * The real headroom is on the server — the sends are sequential per row where they could be one
 * batched call — but this makes the common roster stop failing without touching that.
 */
export const MAX_BATCH = 100;

/**
 * How many times one batch is attempted before its rows are recorded as not sent.
 *
 * For the transient case only — a dropped connection somewhere in eleven minutes of uploading.
 * A request the server refuses will be refused three times just as fast.
 */
const BATCH_ATTEMPTS = 3;

/** Multiplied by the attempt number, so the waits are 1s then 2s. */
const RETRY_BACKOFF_MS = 1000;

/**
 * Refreshed after every batch, not just at the end.
 *
 * The counts are their own queries now that they come from the database rather than from the
 * length of the roster array, so refetching the rows alone would leave the header describing the
 * roster as it was before the upload.
 */
const AFFECTED_QUERIES = [
    "OrganizationById",
    "OrganizationRoster",
    "OrganizationRosterCount",
];

const runs = new Map<string, AutoEnrollRun>();
const listeners = new Set<() => void>();

const emit = (): void => {
    for (const listener of listeners) listener();
};

const subscribe = (onChange: () => void): (() => void) => {
    listeners.add(onChange);
    return () => {
        listeners.delete(onChange);
    };
};

/*
 * Every write replaces the run object rather than mutating it, so `useSyncExternalStore` sees a
 * new reference exactly when something changed and the same one when nothing did. Mutating in
 * place would leave subscribers rendering stale rows; returning a fresh object every read would
 * re-render them forever.
 */
const update = (organizationId: string, change: (run: AutoEnrollRun) => AutoEnrollRun): void => {
    const current = runs.get(organizationId);
    if (!current) return;
    runs.set(organizationId, change(current));
    emit();
};

const settledCount = (statuses: Map<number, AutoEnrollRowResult>): number => {
    let settled = 0;
    for (const { status } of statuses.values()) {
        if (status !== "QUEUED" && status !== "SENDING") settled += 1;
    }
    return settled;
};

const setStatuses = (organizationId: string, entries: [number, AutoEnrollRowResult][]): void => {
    update(organizationId, (run) => {
        const statuses = new Map(run.statuses);
        for (const [
            line,
            result,
        ] of entries)
            statuses.set(line, result);
        return { ...run, statuses, settled: settledCount(statuses) };
    });
};

/**
 * The run as it stands right now, without subscribing.
 *
 * For callers outside a render — the runner itself, and tests that need to look at a run while it
 * is still going, where a hook's value is whatever React last committed rather than what the store
 * holds. Components should use `useAutoEnrollRun` so they re-render when it moves.
 */
export const getAutoEnrollRun = (organizationId: string): AutoEnrollRun | undefined => runs.get(organizationId);

/** The run for one organization, or undefined when nothing has been uploaded there this session. */
export const useAutoEnrollRun = (organizationId: string): AutoEnrollRun | undefined =>
    useSyncExternalStore(
        subscribe,
        () => runs.get(organizationId),
        () => undefined,
    );

/**
 * Records a parsed file as the organization's current upload.
 *
 * Invalid rows get their verdict immediately: the row is not going to be sent, and saying so on
 * arrival is what lets the operator start fixing the spreadsheet before pressing anything.
 */
export const setAutoEnrollUpload = (organizationId: string, fileName: string, rows: AutoEnrollCsvRow[]): void => {
    const statuses = new Map<number, AutoEnrollRowResult>();
    for (const row of rows) {
        if (row.error) statuses.set(row.line, { status: "INVALID", message: row.error });
    }
    runs.set(organizationId, {
        organizationId,
        fileName,
        rows,
        statuses,
        total: rows.filter((row) => !row.error).length,
        settled: settledCount(statuses),
        running: false,
    });
    emit();
};

export const clearAutoEnrollRun = (organizationId: string): void => {
    runs.delete(organizationId);
    emit();
};

/**
 * Sends the valid rows in batches, marking each one as it goes.
 *
 * Every batch is attempted, and one that fails does not stop the ones after it. This is what
 * "we uploaded 8,500 and the portal says 5,000" was: the loop used to sit under a single `try`,
 * so the first batch to throw abandoned every remaining batch. Batches are independent
 * transactions on the server, so there was never a reason for batch 51 to prevent batch 52.
 *
 * The toasts belong to the run rather than to the page, on purpose — an operator who started a
 * ten-minute upload and went to look at something else should still be told how it ended.
 */
export const startAutoEnrollRun = async (
    client: ApolloClient,
    { organizationId, defaultBrand }: { organizationId: string; defaultBrand: string },
): Promise<void> => {
    const run = runs.get(organizationId);
    if (!run || run.running) return;

    const valid = run.rows.filter((row) => !row.error);
    if (valid.length === 0) return;

    update(organizationId, (current) => ({
        ...current,
        running: true,
        statuses: new Map(current.statuses),
    }));
    setStatuses(
        organizationId,
        valid.map((row) => [
            row.line,
            { status: "QUEUED" as const },
        ]),
    );

    /*
     * Only the valid rows are sent, and a repeat is not valid, so every email here belongs to
     * exactly one line — which is what lets the server's answer, which is keyed by email, be put
     * back against the row it came from.
     */
    const lineOf = new Map(
        valid.map((row) => [
            row.email,
            row.line,
        ]),
    );

    const collected: { email: string; status: string; message?: string | null }[] = [];
    /** Welcome emails SES accepted, summed across batches — see the shortfall warning below. */
    let welcomeEmailsSent = 0;
    /** The last error from a batch that exhausted its attempts, for the failure message. */
    let failure: unknown;
    let failedRows = 0;

    for (let start = 0; start < valid.length; start += MAX_BATCH) {
        const batch = valid.slice(start, start + MAX_BATCH);
        let batchError: unknown;

        setStatuses(
            organizationId,
            batch.map((row) => [
                row.line,
                { status: "SENDING" as const },
            ]),
        );

        for (let attempt = 1; attempt <= BATCH_ATTEMPTS; attempt++) {
            try {
                const response = await client.mutate({
                    mutation: BulkAutoEnrollDocument,
                    variables: {
                        input: {
                            organizationId,
                            // The CSV's brand strings are validated against the same
                            // set of values as the enum, so the cast is safe.
                            rows: batch.map(
                                (row) => toEnrollRow(row, defaultBrand) as { email: string; brand: string | null },
                            ),
                        },
                    },
                    refetchQueries: AFFECTED_QUERIES,
                    awaitRefetchQueries: true,
                });
                const results = response.data?.bulkAutoEnroll.results ?? [];
                collected.push(...results);
                welcomeEmailsSent += response.data?.bulkAutoEnroll.welcomeEmailsSent ?? 0;
                setStatuses(
                    organizationId,
                    results.flatMap((result) => {
                        const line = lineOf.get(result.email);
                        return line === undefined
                            ? []
                            : [
                                  [
                                      line,
                                      { status: result.status as AutoEnrollRowStatus, message: result.message },
                                  ] as [number, AutoEnrollRowResult],
                              ];
                    }),
                );
                batchError = undefined;
                break;
            } catch (error) {
                batchError = error;
                // Backs off between attempts, since the case this exists for is a blip rather
                // than a rejection: a request that failed instantly will fail again instantly.
                if (attempt < BATCH_ATTEMPTS) {
                    await new Promise((resolve) => setTimeout(resolve, RETRY_BACKOFF_MS * attempt));
                }
            }
        }

        if (batchError) {
            failure = batchError;
            failedRows += batch.length;
            /*
             * Named per row rather than counted, so the table says which people to try again
             * instead of leaving the operator to work it out from a total.
             */
            for (const row of batch) {
                collected.push({ email: row.email, status: "NOT_SENT", message: "Could not be sent — try again" });
            }
            setStatuses(
                organizationId,
                batch.map((row) => [
                    row.line,
                    { status: "NOT_SENT" as const, message: "Could not be sent — try again" },
                ]),
            );
        }
    }

    update(organizationId, (current) => ({ ...current, running: false }));

    const enrolled = collected.filter((r) => r.status === "ENROLLED" || r.status === "ACTIVATED").length;
    const invited = collected.filter((r) => r.status === "ENROLLED").length;
    // "Enrolled" does not mean "has an account": an invited person becomes a
    // user on their first sign-in, so say so rather than let the unchanged
    // Users count look like a failure.
    const outcome =
        invited > 0
            ? `Enrolled ${enrolled} of ${collected.length}. ${invited} join on first sign-in.`
            : `Enrolled ${enrolled} of ${collected.length}.`;

    if (failure && !isTeardownError(failure)) {
        /*
         * Not `reportQueryFailure`, which shows the error's own message and drops the one passed
         * to it. That is right for a query that either worked or did not, and wrong here: an
         * operator who sees "Failed to fetch" has no idea that fifty people were enrolled and a
         * hundred were not. The outcome leads, and the cause rides along.
         */
        const cause = failure instanceof Error ? failure.message : "the upload was interrupted";
        toast.error(
            `${outcome} ${failedRows} could not be sent (${cause}) — upload the same file again to finish them.`,
        );
    } else if (!failure) {
        toast.success(outcome);
    }

    /*
     * The welcome mail is a separate outcome from the enrollment, and it has to be said out loud
     * when it goes wrong.
     */
    if (welcomeEmailsSent < enrolled) {
        toast.error(
            `${enrolled - welcomeEmailsSent} of ${enrolled} welcome emails were not sent. ` +
                `Everyone is enrolled — do not re-upload. Check the sender configuration before re-sending.`,
        );
    }

    /*
     * Best effort, and deliberately not awaited into the caller's error path.
     *
     * Each batch already refreshed these, so this is for the run where every batch failed and
     * nothing refetched at all. A refresh that does not land shows the previous numbers rather
     * than wrong ones, and the enrollment's own outcome has already been reported.
     */
    await client.refetchQueries({ include: AFFECTED_QUERIES }).catch(() => undefined);
};
